import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import Badge from './Badge';
import { PiUserLight, PiBuildingLight, PiCaretUp, PiCaretDown, PiSpinnerGapLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import ClientAvatar from '../../Clients/ClientAvatar';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import ProfileImage from '../../Header/ProfileImage';
import Button from './Button';

interface EmptyStateProps {
  icon?: React.ReactNode;
  headline?: string;
  subtext?: string;
  button?: {
    label: string;
    onClick: () => void;
  };
}

interface TableProps {
  columns: string[];
  data: Record<string, any>[];
  hiddenColumns?: Set<string>;
  onRowClick?: (row: any) => void;
  renderRow?: (row: any, rowIndex: number) => React.ReactNode;
  renderCell?: (column: string, value: any, row: any) => React.ReactNode;
  enableSelection?: boolean;
  selectedIds?: string[];
  onSelect?: (id: string) => void;
  onSelectAll?: () => void;
  max?: string;
  min?: string;
  actions?: { label: string; onClick: (row: any) => void; icon: React.ReactNode; disabled?: (row: any) => boolean; isDestructive?: boolean; hidden?: (row: any) => boolean }[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  emptyState?: EmptyStateProps;
  sortableColumns?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  total_items?: number;
  total_showing_items?: number,
  setIsShowing?: boolean;
  showingLabel?: string;
  selectedCount?: number;
  resizableColumns?: boolean;
  defaultColumnWidths?: Record<string, number>;
  maxColumnWidths?: Record<string, number>;
  storageKeyPrefix?: string;
  rowSize?: 'compact' | 'default' | 'large';
  infoText?: string;
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  hiddenColumns = new Set(),
  onRowClick,
  renderRow,
  enableSelection = false,
  selectedIds = [],
  onSelect,
  onSelectAll,
  max = 'max-h-[calc(100vh-150px)]',
  min = 'min-h-[calc(100vh-150px)]',
  actions,
  hasMore,
  isLoadingMore,
  onLoadMore,
  emptyState,
  sortableColumns = [],
  sortBy,
  sortOrder,
  onSort,
  total_items,
  total_showing_items,
  setIsShowing = false,
  showingLabel,
  selectedCount,
  resizableColumns = false,
  defaultColumnWidths = {},
  maxColumnWidths = {},
  renderCell,
  storageKeyPrefix = '',
  rowSize = 'default',
  infoText,
}) => {
  const { t } = useTranslation();
  const [menuState, setMenuState] = useState<{ id: string | null; above: boolean; top: number; left: number; ready: boolean }>({ id: null, above: false, top: 0, left: 0, ready: false });
  const menuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizingColRef = useRef<string | null>(null);
  const startXRef = useRef(0);

  // Initialize column widths from localStorage or defaults
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (!resizableColumns) return {};

    const baseKey = storageKeyPrefix ? `${storageKeyPrefix}-${columns.join(',')}` : columns.join(',');
    const storageKey = `table-column-widths-${baseKey}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored column widths:', e);
      }
    }

    // Use provided defaults or calculate proportional widths
    const displayColumns = enableSelection && !columns.includes('Select')
      ? ['Select', ...columns]
      : columns;

    const defaults: Record<string, number> = {};
    displayColumns.forEach((col) => {
      if (defaultColumnWidths[col]) {
        // Add margin to the provided default width (reduce slightly from provided value)
        defaults[col] = Math.max(100, defaultColumnWidths[col] - 20);
      } else if (col === 'Select' || col === 'Actions') {
        defaults[col] = 48; // Fixed width for checkboxes and actions
      } else {
        // Calculate width based on header text length with margin
        // Rough estimate: ~8px per character + 24px padding + 16px margin
        const estimatedWidth = col.length * 8 + 40;
        defaults[col] = Math.max(100, estimatedWidth);
      }
    });

    return defaults;
  });

  // Save column widths to localStorage
  const saveColumnWidths = (widths: Record<string, number>) => {
    if (!resizableColumns) return;
    const baseKey = storageKeyPrefix ? `${storageKeyPrefix}-${columns.join(',')}` : columns.join(',');
    const storageKey = `table-column-widths-${baseKey}`;
    localStorage.setItem(storageKey, JSON.stringify(widths));
  };

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent, column: string) => {
    if (!resizableColumns) return;
    e.preventDefault();
    setIsResizing(true);
    resizingColRef.current = column;
    startXRef.current = e.clientX;
  };

  // Handle resize move
  useEffect(() => {
    if (!isResizing || !resizingColRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!tableRef.current) return;

      const delta = e.clientX - startXRef.current;
      const currentCol = resizingColRef.current;

      // Type guard: ensure currentCol is not null
      if (!currentCol) return;

      setColumnWidths((prev) => {
        const newWidths = { ...prev };
        const currentWidth = newWidths[currentCol] || 0;
        const minWidth = currentCol === 'Select' || currentCol === 'Actions' ? 48 : 100;
        const maxWidth = maxColumnWidths[currentCol];
        let newWidth = Math.max(minWidth, currentWidth + delta);
        if (maxWidth) newWidth = Math.min(maxWidth, newWidth);

        // Only update the resized column — keep all others exactly as they are
        // so they don't shift when the table total width changes
        newWidths[currentCol] = newWidth;

        return newWidths;
      });

      startXRef.current = e.clientX;
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizingColRef.current = null;

      // Save widths on resize end
      setColumnWidths((prev) => {
        saveColumnWidths(prev);
        return prev;
      });
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, resizableColumns]);

  // Infinite scroll disabled - using explicit "Load More" button instead
  // useEffect(() => {
  //   if (!hasMore || isLoadingMore) return;
  //
  //   const handleScroll = () => {
  //     const container = tableContainerRef.current;
  //     if (!container) return;
  //
  //     const scrollTop = container.scrollTop;
  //     const scrollHeight = container.scrollHeight;
  //     const clientHeight = container.clientHeight;
  //     const scrollThreshold = scrollHeight - clientHeight - 300;
  //
  //     if (scrollTop >= scrollThreshold && hasMore && !isLoadingMore) {
  //       onLoadMore?.();
  //     }
  //   };
  //
  //   const container = tableContainerRef.current;
  //   if (container) {
  //     container.addEventListener('scroll', handleScroll);
  //     return () => container.removeEventListener('scroll', handleScroll);
  //   }
  // }, [hasMore, isLoadingMore]);

  useLayoutEffect(() => {
    if (!menuState.id) return;
    if (menuBtnRefs.current[menuState?.id] && menuRef.current) {
      const btn = menuBtnRefs.current[menuState?.id]!;
      const rect = btn.getBoundingClientRect();
      const menuHeight = menuRef.current.offsetHeight;
      const menuWidth = menuRef.current.offsetWidth;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const above = spaceBelow < menuHeight && rect.top > menuHeight;
      const top = above ? rect.top - menuHeight - 4 : rect.bottom + 4;
      const left = Math.max(8, rect.right - menuWidth);
      setMenuState((prev) => ({ ...prev, above, top, left, ready: true }));
    }
  }, [menuState.id]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const isClickInsideMenu = menuRef.current?.contains(target);
      const isClickOnAnyButton = Object.values(menuBtnRefs.current).some(
        (btn) => btn && btn.contains(target)
      );

      if (!isClickInsideMenu && !isClickOnAnyButton) {
        setMenuState({ id: null, above: false, top: 0, left: 0, ready: false });
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Close menu on scroll since it's fixed-positioned via portal
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !menuState.id) return;
    const handleScroll = () => {
      setMenuState({ id: null, above: false, top: 0, left: 0, ready: false });
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [menuState.id]);

  const displayColumns = enableSelection
    ? columns.includes('Select')
      ? columns
      : ['Select', ...columns]
    : columns;

  const sortedActions = actions
    ? [...actions].sort((a, b) => (a.isDestructive === b.isDestructive ? 0 : a.isDestructive ? 1 : -1))
    : [];


  const handleSortClick = (column: string) => {
    if (!sortableColumns.includes(column) || !onSort) return;
    const mappedSortBy = column.toLowerCase().replace(/\s+/g, '_');
    const newSortOrder = sortBy === mappedSortBy && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(mappedSortBy, newSortOrder);
  };


  return (
    <div className={`flex flex-col h-full`}>
      {total_items != null && total_showing_items != null && setIsShowing === true && (
        <div className="flex items-center justify-between mt-2 mb-2 mx-2 flex-shrink-0">
          <Badge color="gray">
            {t("Showing")} {total_showing_items} {t("of")} {total_items} {showingLabel || t("items")}{selectedCount != null && selectedCount > 0 ? ` | ${selectedCount} ${t('selected')}` : ''}
          </Badge>
          {infoText && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {infoText}
            </span>
          )}
        </div>
      )}
      <div ref={tableContainerRef} className={clsx('flex-1 overflow-y-auto overflow-x-auto custom-scrollbar-main', max, min)} style={{ userSelect: isResizing ? 'none' : 'auto' }}>
        <table
          ref={tableRef}
          className={clsx(`divide-y divide-gray-200 dark:divide-gray-700`)}
          style={{
            borderCollapse: 'collapse',
            tableLayout: resizableColumns ? 'fixed' : 'auto',
            width: resizableColumns
              ? `max(100%, ${Object.values(columnWidths).reduce((sum, w) => sum + w, 0)}px)`
              : '100%',
          }}
        >
          {resizableColumns && (
            <colgroup>
              {displayColumns.map((col) => {
                const w = columnWidths[col];
                return <col key={col} style={w ? { width: `${w}px` } : (col === 'Select' || col === 'Actions') ? { width: '48px' } : undefined} />;
              })}
            </colgroup>
          )}
          <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0 z-10">
            <tr>
              {displayColumns.map((col, idx) => {
                const colWidth = columnWidths[col];
                const isLastColumn = idx === displayColumns.length - 1;
                // Calculate proper minWidth based on column type
                const headerMinWidth = col === 'Select' || col === 'Actions' ? 48 : 100;

                return (
                  <th
                    key={col}
                    scope="col"
                    className={clsx(
                      'relative text-left text-xs text-gray-500 dark:text-gray-400 capitalize tracking-wider',
                      col === 'Select' ? 'px-0' : 'px-1 py-2.5',
                      col === 'Select' && 'py-2.5',
                      idx === 0 && !enableSelection && 'pl-4',
                      hiddenColumns.has(col) && 'hidden lg:table-cell',
                      col === 'Select' && !resizableColumns && 'w-12',
                    )}
                    style={col === 'Select'
                      ? { width: '48px', minWidth: '48px', maxWidth: '48px', flexShrink: 0, paddingLeft: '0px', paddingRight: '0px' }
                      : colWidth ? { width: `${colWidth}px`, minWidth: `${headerMinWidth}px`, ...(maxColumnWidths[col] ? { maxWidth: `${maxColumnWidths[col]}px` } : {}), flexShrink: 0 } : (maxColumnWidths[col] ? { maxWidth: `${maxColumnWidths[col]}px` } : undefined)
                    }
                  >
                    <div className={clsx('flex items-center h-full', col === 'Select' ? 'justify-center w-full' : 'justify-between')}>
                      <div className={col === 'Select' ? 'flex items-center justify-center w-full' : 'flex-1 min-w-0 overflow-hidden'}>
                        {col === 'Select' && enableSelection ? (
                          <input
                            type="checkbox"
                            className={clsx(
                              'h-4 w-4 rounded border-2 focus:ring-2 focus:ring-offset-1 cursor-pointer transition-all',
                              selectedIds.length === data.length && data.length > 0
                                ? 'border-primary-600 bg-primary-600 text-white focus:ring-primary-600'
                                : 'border-gray-300 text-primary-600 focus:ring-primary-500'
                            )}
                            checked={selectedIds.length === data.length && data.length > 0}
                            onChange={onSelectAll}
                          />
                        ) : sortableColumns.includes(col) ? (
                          <button
                            onClick={() => handleSortClick(col)}
                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 min-w-0 max-w-full"
                          >
                            <span className='capitalize whitespace-nowrap truncate'>{t(col)}</span>
                            <div className="flex flex-col ml-1">
                              <PiCaretUp
                                className={clsx(
                                  'h-3 w-3',
                                  sortBy === col.toLowerCase().replace(/\s+/g, '_') && sortOrder === 'asc'
                                    ? 'text-gray-700 dark:text-gray-200 opacity-100'
                                    : 'text-gray-400 dark:text-gray-500 opacity-50'
                                )}
                              />
                              <PiCaretDown
                                className={clsx(
                                  'h-3 w-3',
                                  sortBy === col.toLowerCase().replace(/\s+/g, '_') && sortOrder === 'desc'
                                    ? 'text-gray-700 dark:text-gray-200 opacity-100'
                                    : 'text-gray-400 dark:text-gray-500 opacity-50'
                                )}
                              />
                            </div>
                          </button>
                        ) : col === 'Actions' ? (
                          null
                        ) : (
                          <span className='capitalize whitespace-nowrap truncate'>{t(col)}</span>
                        )}
                      </div>

                      {resizableColumns && !isLastColumn && col !== 'Select' && col !== 'Actions' && (
                        <div
                          onMouseDown={(e) => handleResizeStart(e, col)}
                          className={clsx(
                            'table-resize-handle',
                            isResizing && resizingColRef.current === col && 'resizing'
                          )}
                          title="Drag to resize column"
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {(data.length === 0 && !isLoadingMore) ? (
              <tr className='my-auto'>
                <td colSpan={displayColumns.length} className="text-center py-28">
                  <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
                    {emptyState?.icon && (
                      <div className="text-gray-400 dark:text-gray-500">
                        {React.cloneElement(emptyState.icon as React.ReactElement<any>, { size: 48 })}
                      </div>
                    )}
                    {emptyState?.headline && (
                      <h2 className="text-lg text-gray-900 dark:text-gray-100">
                        {emptyState.headline}
                      </h2>
                    )}
                    {emptyState?.subtext && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                        {emptyState.subtext}
                      </p>
                    )}
                    {emptyState?.button && (
                      <Button
                        variant="primary"
                        size="small"
                        onClick={emptyState.button.onClick}
                        className='mt-4'
                      >
                        {emptyState.button.label}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIdx) => {
                if (renderRow) return renderRow(row, rowIdx);
                return (
                  <tr
                    key={row.id || rowIdx}
                    className={clsx(
                      'group cursor-pointer transition-colors duration-200',
                      rowIdx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800',
                      'hover:bg-gray-100 dark:hover:bg-gray-700',
                      row.className
                    )}
                    style={row.style}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {displayColumns.map((col: any, colIdx: number) => {
                      let cellContent = row[col];

                      // Use custom renderCell if provided
                      if (renderCell) {
                        const customContent = renderCell(col, cellContent, row);
                        if (customContent !== undefined && customContent !== null) {
                          cellContent = customContent;
                        }
                      }

                      if (col === 'Select' && enableSelection) {
                        cellContent = (
                          <div className="flex items-center justify-center w-full h-full">
                            <input
                              type="checkbox"
                              disabled={row.FolderType === 'Locked' ? true : false}
                              className={clsx(
                                'h-4 w-4 rounded border-2 focus:ring-2 focus:ring-offset-1 cursor-pointer transition-all',
                                selectedIds.includes(row.id)
                                  ? 'border-primary-600 bg-primary-600 text-white focus:ring-primary-600'
                                  : 'border-gray-300 text-primary-600 focus:ring-primary-500'
                              )}
                              checked={selectedIds.includes(row.id)}
                              onChange={() => onSelect && onSelect(row.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        );
                      } else if (col === 'Client' && typeof row[col] === 'object') {
                        const { name, email, clientType, companyLogo } = row[col];
                        cellContent = (
                          <div className="flex items-center gap-2">
                            <ClientAvatar email={email} name={name} clientType={clientType} companyLogo={companyLogo} />
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{email}</div>
                            </div>
                          </div>
                        );
                      } else if (col === 'Profile' && typeof row[col] === 'object') {
                        const { name, email, profilePicture, initials } = row[col];
                        cellContent = (
                          <div className="flex items-center gap-2">
                            <ProfileImage profilePicture={profilePicture} email={email} initials={initials} name={name} />
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{email}</div>
                            </div>
                          </div>
                        );
                      } else if (col === 'Client Type') {
                        cellContent = (
                          <div className="flex items-center gap-2">
                            {row[col] === 'Business' ? (
                              <PiBuildingLight className="text-secondary-500" size={16} />
                            ) : (
                              <PiUserLight className="text-secondary-500" size={16} />
                            )}
                            <span>{t(row[col])}</span>
                          </div>
                        );
                      } else if (col === 'Value' && typeof row[col] === 'number') {
                        cellContent = <span className="">${row[col].toLocaleString()}</span>;
                      } else if (col === 'Case Count' && typeof row[col] === 'number') {
                        cellContent = <span className="">{row[col].toLocaleString()}</span>;
                      } else if (col === 'Actions' && sortedActions) {
                        const visibleActions = sortedActions.filter((action) => !(typeof action.hidden === 'function' && action.hidden(row)));
                        cellContent = visibleActions.length === 0 ? null : (
                          <div className="relative">
                            <button
                              ref={(el) => { if (el) menuBtnRefs.current[row.id] = el; }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const isSame = menuState.id === row.id;
                                setMenuState({
                                  id: isSame ? null : row.id,
                                  above: false,
                                  top: 0,
                                  left: 0,
                                  ready: false,
                                });
                              }}
                              className="text-gray-500 hover:text-gray-700 text-xl p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                            >
                              ⋮
                            </button>
                            {menuState.id === row.id && ReactDOM.createPortal(
                              <div
                                ref={menuRef}
                                id="row-menu"
                                className={clsx(
                                  'fixed min-w-[12rem] w-max bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700',
                                  menuState.above ? 'origin-bottom' : 'origin-top',
                                )}
                                style={{
                                  top: menuState.top,
                                  left: menuState.left,
                                  zIndex: 9999,
                                  opacity: menuState.ready ? 1 : 0,
                                  transform: menuState.ready ? 'scale(1)' : 'scale(0.95)',
                                  transition: 'opacity 120ms ease-out, transform 120ms ease-out',
                                }}
                              >
                                <div className="py-1">
                                  {sortedActions
                                    .filter((action) => !(typeof action.hidden === 'function' && action.hidden(row)))
                                    .map((action, idx, visibleActions) => {
                                    const isDisabled = typeof action.disabled === 'function' ? action.disabled(row) : false;
                                    const isLastDestructive = action.isDestructive && idx === visibleActions.length - 1;
                                    return (
                                      <React.Fragment key={idx}>
                                        {isLastDestructive && <hr className="m-1 border-gray-200 dark:border-gray-700" />}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isDisabled) action.onClick(row);
                                            setMenuState({ id: null, above: false, top: 0, left: 0, ready: false });
                                          }}
                                          disabled={isDisabled}
                                          className={clsx(
                                            'flex items-center gap-2 px-4 py-2 text-sm w-full text-left whitespace-nowrap transition',
                                            isDisabled
                                              ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed bg-transparent'
                                              : action.isDestructive
                                                ? 'text-red-700 hover:bg-red-100 dark:hover:bg-red-900/50'
                                                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                          )}
                                        >
                                          {action.icon}
                                          {action.label}
                                        </button>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>,
                              document.body
                            )}
                          </div>
                        );
                      }
                      const colWidth = columnWidths[col];

                      // Calculate proper minWidth based on column type
                      const cellMinWidth = col === 'Select' || col === 'Actions' ? 48 : 100;

                      return (
                        <td
                          key={col}
                          className={clsx(
                            rowSize === 'compact' ? 'py-1' : rowSize === 'large' ? 'py-3' : 'py-2.5',
                            'text-sm text-gray-900 dark:text-gray-200',
                            col === 'Select' ? 'px-0' : 'px-1',
                            colIdx === 0 && !enableSelection && 'pl-4',
                            hiddenColumns.has(col) && 'hidden lg:table-cell',
                            col === 'Select' && !resizableColumns && 'w-12',
                            col === 'Select' && 'text-center',
                            col === 'Actions' && 'text-center',
                            resizableColumns && col === 'Actions' && 'text-center',
                            // Enforce single-line with ellipsis on all data cells
                            col !== 'Actions' && col !== 'Select' && 'truncate whitespace-nowrap overflow-hidden'
                          )}
                          style={col === 'Select'
                            ? { width: '48px', minWidth: '48px', maxWidth: '48px', flexShrink: 0, paddingLeft: '0px', paddingRight: '0px' }
                            : colWidth ? { width: `${colWidth}px`, minWidth: `${cellMinWidth}px`, ...(maxColumnWidths[col] ? { maxWidth: `${maxColumnWidths[col]}px` } : {}), flexShrink: 0 } : (maxColumnWidths[col] ? { maxWidth: `${maxColumnWidths[col]}px` } : undefined)
                          }
                          title={typeof cellContent === 'string' ? cellContent : undefined}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {isLoadingMore && <SkeletonClientItem viewMode="table" count={2} />}
        {hasMore && (
          <div className="sticky bottom-0 left-0 right-0 p-4 text-center bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700" style={{ minWidth: 'fit-content', width: '100%' }}>
            <Button
              variant="outline"
              size="small"
              onClick={onLoadMore}
              disabled={isLoadingMore}
              className="inline-flex items-center gap-2"
            >
              {isLoadingMore ? (
                <>
                  <PiSpinnerGapLight className="animate-spin h-4 w-4" />
                  {t('Loading...')}
                </>
              ) : (
                t('Load More')
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;
